import React, { useState } from 'react';
import { LockIcon, RotateCcwIcon } from 'lucide-react';
import { SectionHeading, SpecSection } from './SectionHeading';
import { Specimen } from './Specimen';
import { ToastDemo } from './ToastDemo';
import { CountdownCardSample, DoneCardSample, InfoCardSample, ProposalCardSample } from './CardVariants';
import { CaptionLine } from '../ui/CaptionLine';
import { AgentThread } from '../ui/AgentThread';
import { LevelBadge } from '../ui/LevelBadge';
import { HouseModeChip } from '../ui/HouseModeChip';
import { ProcessingTag } from '../ui/ProcessingTag';
import { WhyPopover } from '../ui/WhyPopover';
import { SlideToConfirm } from '../ui/SlideToConfirm';
import { NoodstopButton } from '../ui/NoodstopButton';
import { NoodstopSheet } from '../ui/NoodstopSheet';
import { BeheerButton } from '../ui/BeheerButton';
import { actionLevels } from '../../data/levels';
import { houseModes } from '../../data/houseModes';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import { useDb } from '../../hooks/useDb';
import { activeTasks } from '../../utils/dbSelectors';
import { playSound } from '../../utils/sound';
export function ComponentsSection() {
  const threads = useDb(activeTasks);
  const [captionKey, setCaptionKey] = useState(0);
  const [slideKey, setSlideKey] = useState(0);
  const [stopped, setStopped] = useState(false);
  const {
    soundOn
  } = useSpecSettings();
  return <SpecSection id="componenten" data-id="element-994" data-mp="291z4n4fmplgb">
      <SectionHeading id="componenten" index={6} title="Componenten" intro="De bouwstenen voor elk scherm. Alles werkt: schuif, houd vast, open Waarom?." data-id="element-995" data-mp="290xju88t9kle" />

      <div className="mt-14 grid gap-14 lg:grid-cols-12" data-id="element-996" data-mp="2hxevdcl37kho">
        <div className="lg:col-span-7" data-id="element-997" data-mp="2dva42ki42zy7">
          <h3 className="text-[20px] font-semibold text-ink" data-id="element-998" data-mp="2dwcukrbiv0dl">Opgeroepen kaarten</h3>
          <p className="mt-1 text-[15px] text-ink-muted" data-id="element-999" data-mp="2ru1enwxrm3hl">Info, voorstel, Niveau 2-aftelling en gedaan.</p>
          <div className="relative mt-6 rounded-3xl border border-white/[0.06] p-4 sm:p-6" style={{
          background: 'radial-gradient(circle at 50% 40%, rgb(var(--accent) / 0.07), transparent 60%), #0A0D12'
        }} data-id="element-1000" data-mp="2mskgwaakux58">
            <div className="grid gap-4 md:grid-cols-2" data-id="element-1001" data-mp="1thfc256ig62h">
              <InfoCardSample data-id="element-1002" data-mp="1tfujgl3rax79" />
              <ProposalCardSample data-id="element-1003" data-mp="1y8dxtka9pwd4" />
              <CountdownCardSample data-id="element-1004" data-mp="1y9fhue99ht6z" />
              <DoneCardSample data-id="element-1005" data-mp="1yai9wpibm1oh" />
            </div>
          </div>
        </div>

        <div className="space-y-8 lg:col-span-5" data-id="element-1006" data-mp="237h232ifbyk1">
          <Specimen title="Ondertitel" note="Woord voor woord in sync met de spraak, vervaagt na 6 s. 32–40 px op de wandtablet." data-id="element-1007" data-mp="286bsoqmmurep">
            <div className="min-h-[96px]" data-id="element-1008" data-mp="2c9q9vkmb5dtq">
              <CaptionLine key={captionKey} text="Het gaat regenen. Neem een paraplu mee en vertrek om 13:30." size="wall" data-id="element-1009" data-mp="2h7jewabvk1sf" />
            </div>
            <button type="button" onClick={() => setCaptionKey(k => k + 1)} className="mt-2 inline-flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-muted transition-colors duration-150 hover:text-ink" data-id="element-1010" data-mp="hvgyj8yjrniz">
              <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden data-id="element-1011" data-mp="air6vbhj7ljw" /> Opnieuw
            </button>
          </Specimen>

          <Specimen title="Agentdraad" note="Eén draad per actieve agent. De puls reist zo snel als de taak vordert." data-id="element-1012" data-mp="5jenx1ts6yml">
            <div className="space-y-5" data-id="element-1013" data-mp="mehuqwxlvvh">
              {threads.slice(0, 4).map(t => <AgentThread key={t.id} task={t} data-id="element-1014" data-mp="y7istl4j37o8" />)}
            </div>
          </Specimen>

          <Specimen title="Niveaubadges" data-id="element-1015" data-mp="tb24oqxbwtb5">
            <div className="flex flex-wrap gap-2" data-id="element-1016" data-mp="oanvh5jzrdbj">
              {actionLevels.map(l => <LevelBadge key={l.id} level={l.id} data-id="element-1017" data-mp="ociiqqiqjhis" />)}
            </div>
            <p className="mt-4 flex items-center gap-2.5 text-[15px] text-ink-soft" data-id="element-1018" data-mp="kb59e6gfow74">
              <LockIcon className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden data-id="element-1019" data-mp="3927w0z3siv9d" />
              Voordeur: open zelf via de deurbel-app.
            </p>
            <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1020" data-mp="wq3fam8y8fhx">Nooit automatisch: geen knop, geen aftelling, alleen deze tekst.</p>
          </Specimen>

          <Specimen title="Huismodus en verwerking" data-id="element-1021" data-mp="11imqjck5yyjk">
            <div className="flex flex-wrap gap-2" data-id="element-1022" data-mp="msxlv7cv4cej">
              {houseModes.map(m => <HouseModeChip key={m.id} mode={m.id} active={m.id === 'Focus'} data-id="element-1023" data-mp="rqz5pui44fr2" />)}
            </div>
            <div className="mt-3 flex gap-2" data-id="element-1024" data-mp="1fgti6ayis2as">
              <ProcessingTag where="lokaal" data-id="element-1025" data-mp="1ff080lop6zy1" />
              <ProcessingTag where="cloud" data-id="element-1026" data-mp="1kgfu8h2m3ci0" />
            </div>
          </Specimen>

          <Specimen title="Waarom?" note="Aanleiding → context → doel → data → actie en niveau." data-id="element-1027" data-mp="16gojqqbb0e8r">
            <WhyPopover traceId="meeting-lisa" align="left" data-id="element-1028" data-mp="1ahqbyn6szq18" />
          </Specimen>

          <Specimen title="Goedkeuren" note="Alleen voor Niveau 3. Schuif volledig door; loslaten eerder veert terug." data-id="element-1029" data-mp="3r6netz792sty">
            <SlideToConfirm key={slideKey} label="Schuif om PR #142 te mergen" confirmedLabel="Gemerged" onConfirm={() => soundOn && playSound('klaar')} data-id="element-1030" data-mp="3v8qt0vlb0bix" />
            <button type="button" onClick={() => setSlideKey(k => k + 1)} className="mt-3 inline-flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-muted transition-colors duration-150 hover:text-ink" data-id="element-1031" data-mp="3ioli2vfx52rm">
              <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden data-id="element-1032" data-mp="34xyqdn8ksd51" /> Opnieuw
            </button>
          </Specimen>

          <Specimen title="Melding (toast)" data-id="element-1033" data-mp="2zzx0ojgn9fkl">
            <ToastDemo data-id="element-1034" data-mp="2zx116qp5eiu1" />
          </Specimen>

          <Specimen title="Noodstop en Beheer" note="Noodstop staat op elk scherm: 2 s vasthouden. Beheer is klein en stil." data-id="element-1035" data-mp="3eo9x1hjn90fm">
            <div className="flex flex-wrap items-center gap-4" data-id="element-1036" data-mp="39wjl575264m3">
              <NoodstopButton onTrigger={() => setStopped(true)} data-id="element-1037" data-mp="2m77a01q67mh5" />
              <BeheerButton data-id="element-1038" data-mp="2m5e26j3tmw7k" />
            </div>
          </Specimen>
        </div>
      </div>

      <NoodstopSheet open={stopped} onResume={() => setStopped(false)} data-id="element-1039" data-mp="2h6jlbmpezhq3" />
    </SpecSection>;
}